import React, { createContext, useContext, useCallback, useEffect } from 'react';
import { useSession } from './SessionContext';
import {
  getLineChargeTotal,
  getLineListTotal,
  isPartialPaymentEligible,
  getPartialMinPay
} from '../utils/cartPricing';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  // Initialize cart synchronously from localStorage to avoid hydration race on first render
  const [cart, setCart] = React.useState(() => {
    try {
      const raw = window.localStorage.getItem('kiosk_cart');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          console.log('[Cart] Init from localStorage with', parsed.length, 'items');
          return parsed;
        }
      }
    } catch (e) {
      console.warn('[Cart] Failed initial localStorage read', e);
    }
    return [];
  });
  const [hydrated, setHydrated] = React.useState(true);

  // Note: hydration done synchronously above; hydrated is true

  // Persist cart to localStorage on change
  useEffect(() => {
    try {
      window.localStorage.setItem('kiosk_cart', JSON.stringify(cart || []));
    } catch (e) {
      console.warn('[Cart] Failed to persist to localStorage', e);
    }
  }, [cart]);

  // Add item to cart
  const addToCart = useCallback((item) => {
    console.log('=== ADD TO CART ===');
    console.log('Raw item:', JSON.stringify(item, null, 2));
    
    let productId, variationId, cartItemId;
    
    // If product_id and variation_id are already set, use them directly
    if (item.product_id !== undefined && item.variation_id !== undefined) {
      productId = item.product_id;
      variationId = item.variation_id;
      cartItemId = item.cart_item_id || `${productId}-${variationId}`;
    } 
    // If we have a combined ID in the id field (e.g., "18455-18693")
    else if (item.id && typeof item.id === 'string' && item.id.includes('-')) {
      const [pid, vid] = item.id.split('-');
      productId = parseInt(pid, 10) || 0;
      variationId = parseInt(vid, 10) || 0;
      cartItemId = item.id;
    } 
    // Fallback to individual fields
    else {
      productId = item.product_id || 0;
      variationId = item.variation_id || 0;
      cartItemId = item.cart_item_id || `${productId}-${variationId}`;
    }
    
    console.log('Processed IDs - productId:', productId, 'variationId:', variationId, 'cartItemId:', cartItemId);
    
    setCart(prevCart => {
      // Check if item already exists in cart by its ID
      const existingItemIndex = prevCart.findIndex(cartItem => 
        cartItem.id === cartItemId || cartItem.cart_item_id === cartItemId
      );

      if (existingItemIndex >= 0) {
        // Update quantity if item exists (clear partial override — list total / qty changed)
        const newCart = [...prevCart];
        const nextQty = (newCart[existingItemIndex].quantity || 1) + (item.quantity || 1);
        const { pay_line_total: _p, pay_line_cleared: _c, ...rest } = newCart[existingItemIndex];
        newCart[existingItemIndex] = {
          ...rest,
          quantity: nextQty
        };
        console.log('Updated cart item:', newCart[existingItemIndex]);
        console.log('Updated cart state:', JSON.stringify(newCart, null, 2));
        return newCart;
      } else {
        // Add new item to cart with all required fields
        const newItem = {
          ...item,
          id: cartItemId,
          productId: productId,  // Add productId as a direct property
          product_id: productId,  // Keep for backward compatibility
          variationId: variationId,  // Add variationId as a direct property
          variation_id: variationId,  // Keep for backward compatibility
          cart_item_id: cartItemId,
          quantity: item.quantity || 1,
          // Get image URL from the same source as the products page
          image: item.images?.[0]?.src || item.image || ''
        };
        // Remove the images array if it exists
        delete newItem.images;
        const newCart = [...prevCart, newItem];
        console.log('Added new item to cart:', JSON.stringify(newItem, null, 2));
        console.log('New cart state:', JSON.stringify(newCart, null, 2));
        return newCart;
      }
    });
  }, []);

  // Remove item from cart
  const removeFromCart = useCallback((cartItemId) => {
    console.log('=== REMOVE FROM CART ===');
    console.log('Removing item with ID:', cartItemId);
    
    setCart(prevCart => {
      const newCart = prevCart.filter(item => 
        item.id !== cartItemId && item.cart_item_id !== cartItemId
      );
      console.log('Cart after removal:', JSON.stringify(newCart, null, 2));
      return newCart;
    });
  }, []);

  // Update item quantity
  const updateQuantity = useCallback((cartItemId, newQuantity) => {
    console.log('=== UPDATE QUANTITY ===');
    console.log('Updating item ID:', cartItemId, 'New quantity:', newQuantity);
    
    if (newQuantity < 1) {
      removeFromCart(cartItemId);
      return;
    }
    
    setCart(prevCart => {
      const newCart = prevCart.map(item => {
        if (item.id === cartItemId || item.cart_item_id === cartItemId) {
          const { pay_line_total, pay_line_cleared, ...base } = item;
          // Partial line amount was for the old line total; clear when quantity changes
          const updatedItem =
            (item.quantity || 1) !== newQuantity && (pay_line_total != null || pay_line_cleared)
              ? { ...base, quantity: newQuantity }
              : { ...item, quantity: newQuantity };
          console.log('Updating quantity for item:', JSON.stringify(updatedItem, null, 2));
          return updatedItem;
        }
        return item;
      });
      console.log('Cart after quantity update:', JSON.stringify(newCart, null, 2));
      return newCart;
    });
  }, [removeFromCart]);

  const updateItemPayLineTotal = useCallback((cartItemId, payLineTotal) => {
    setCart((prev) =>
      prev.map((item) => {
        if (item.id !== cartItemId && item.cart_item_id !== cartItemId) return item;
        if (!isPartialPaymentEligible({ ...item })) return item;
        if (payLineTotal == null || payLineTotal === '') {
          const { pay_line_total: _d, pay_line_cleared: _c, ...rest } = item;
          return rest;
        }
        const n = parseFloat(String(payLineTotal).replace(/,/g, ''));
        if (!Number.isFinite(n)) return item;
        const list = getLineListTotal({ ...item });
        const min = getPartialMinPay({ ...item });
        if (min == null) return item;
        const clamped = Math.min(list, Math.max(min, n));
        return { ...item, pay_line_total: clamped, pay_line_cleared: false };
      })
    );
  }, []);

  const clearItemPayLineTotal = useCallback((cartItemId) => {
    setCart((prev) =>
      prev.map((item) => {
        if (item.id !== cartItemId && item.cart_item_id !== cartItemId) return item;
        if (!isPartialPaymentEligible({ ...item })) return item;
        return { ...item, pay_line_cleared: true, pay_line_total: undefined };
      })
    );
  }, []);

  // Get session context
  const { resetSession } = useSession();

  // Clear cart and reset session
  const clearCart = useCallback(() => {
    setCart([]);
    resetSession();
  }, [resetSession]);

  // Calculate cart total (respects per-line pay_line_total for eligible lines)
  const cartTotal = cart.reduce((total, item) => total + getLineChargeTotal(item), 0);

  // Calculate item count
  const itemCount = cart.reduce((count, item) => count + (item.quantity || 1), 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        setCart, // Make setCart available to consumers
        addToCart,
        removeFromCart,
        updateQuantity,
        updateItemPayLineTotal,
        clearItemPayLineTotal,
        clearCart,
        cartTotal,
        itemCount,
        hydrated
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
